import type {
  GetListParams,
  GetListResult,
  GetOneResult,
  CreateResult,
  UpdateResult,
  DeleteResult,
  UpdateParams,
  GetOneParams,
  DeleteParams,
} from "ra-core";
import { toRaJob, type JobRaRecord } from "./Mappers";
import type { PageQuery } from "@/domain/port/PageQuery";
import type { JobService } from "@/domain/port/JobService";
import { asJobId } from "@/domain/model/Ids";
import type { UpdateJobCommand } from "@/domain/model/Job";

export function makeJobAdapter(jobs: JobService) {
  return {
    async getList(
      _resource: string,
      params: GetListParams,
    ): Promise<GetListResult<JobRaRecord>> {
      const { page, perPage } = params.pagination ?? { page: 1, perPage: 25 };
      const { field, order } = params.sort ?? {
        field: "createdAt",
        order: "DESC",
      };
      const filter = params.filter ?? {};
      const q: PageQuery = {
        page,
        pageSize: perPage,
        sortBy: field,
        sortDir: order.toLowerCase() as "asc" | "desc",
        filter,
      };
      const res = await jobs.list(q);
      return { data: res.items.map(toRaJob), total: res.total };
    },

    async create(
      _resource: string,
      { data }: { data: any },
    ): Promise<CreateResult<JobRaRecord>> {
      const created = await jobs.create(data);
      return { data: toRaJob(created) };
    },

    async getOne(
      _resource: string,
      params: GetOneParams,
    ): Promise<GetOneResult<JobRaRecord>> {
      const item = await jobs.getById(asJobId(params.id));
      return { data: toRaJob(item) };
    },

    async update(
      _resource: string,
      params: UpdateParams,
    ): Promise<UpdateResult<JobRaRecord>> {
      const updated = await jobs.update(
        asJobId(params.id),
        params.data as UpdateJobCommand, // ideally already transformed in the view
      );
      return { data: toRaJob(updated) };
    },

    async delete(
      _resource: string,
      params: DeleteParams,
    ): Promise<DeleteResult<JobRaRecord>> {
      const deleted = await jobs.delete(asJobId(params.id));
      return { data: toRaJob(deleted) };
    },
  };
}
